import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import { Button } from 'antd'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faTag } from '@fortawesome/pro-solid-svg-icons/faTag'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/lms-profiles.action'
import { ILmsProfileModel } from 'models/lmsProfile.model'
import { createRouteProfileEdit, ProfileRoutes } from 'enum/routes'
import UploadModal from 'components/configuration/components/lms-profiles/upload-modal/UploadModal'
import downloadLmsProfiles from 'api/lms-profiles/Download'
import { downloadFile } from 'helpers/downloadFile.helper'
import { LmsProfileTypes } from 'enum/lms-profile-types.enum'
import DeleteProfileButton from '../delete-profile-button/DeleteProfileButton'
import { RankmiTableWrapper, Options, Profile } from './styled'

const List = () => {
  const { t } = useTranslation()
  const translationPrefix = 'configuration.lmsProfiles'
  const dispatch = useDispatch()
  const history = useHistory()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [paginatedList, setPaginatedList] = useState<ILmsProfileModel[]>([])
  const {
    list,
    total,
    loading,
    sort: originalSort,
  } = useSelector(({ lmsProfiles }: IMainState) => lmsProfiles)
  const [sort, setSort] = useState(originalSort)

  useEffect(() => {
    dispatch({
      type: fromActions.LMS_PROFILES_LIST_GET,
      payload: { page: 1, searchText, sort },
    })
  }, [dispatch, searchText, sort])

  useEffect(() => {
    setPage(1)
  }, [list])

  useEffect(() => {
    setPaginatedList(list)
  }, [page, list])

  const onSortChange = (value: { order: string; field: string }) => {
    if (value.order) {
      const newOrder = value.order === 'ascend' ? 'asc' : 'desc'
      setSort([{ key: value.field, order: newOrder }])
    } else {
      setSort([])
    }
  }

  const handleDownload = async () => {
    setDownloadLoading(true)
    try {
      const { data } = await downloadLmsProfiles()
      downloadFile(data)
    } finally {
      setDownloadLoading(false)
    }
  }

  const locale = {
    header: {
      addButton: t(`${translationPrefix}.newProfile.buttonV2`),
      rowsCount: t(`${translationPrefix}.rowsCount`),
      rowsCount_plural: t(`${translationPrefix}.rowsCount_plural`),
    },
  }

  const actions = [
    {
      label: t('configuration.lmsProfiles.manage.import'),
      onClick: () => setShowUploadModal(true),
    },
    {
      label: t('configuration.lmsProfiles.manage.export'),
      onClick: handleDownload,
      disabled: downloadLoading,
    },
  ]

  const profileType = {
    [LmsProfileTypes.participants]: t(
      `${translationPrefix}.list.table.assignmentMode.perParticipant`
    ),
    [LmsProfileTypes.segments]: t(
      `${translationPrefix}.list.table.assignmentMode.perSegment`
    ),
    [LmsProfileTypes.allUsers]: t(
      `${translationPrefix}.list.table.assignmentMode.allUsers`
    ),
  }

  const columns = [
    {
      title: t(`${translationPrefix}.list.table.headers.lmsProfile`),
      key: 'name',
      sorteable: true,
      render: (_: string, data: ILmsProfileModel) => (
        <Profile color={data.color}>{data.name}</Profile>
      ),
    },
    {
      title: t(`${translationPrefix}.list.table.headers.code`),
      key: 'code',
      sorteable: true,
      render: (code: string) => code,
    },
    {
      title: t(`${translationPrefix}.list.table.headers.coursesAvailable`),
      key: 'total_courses',
      sorteable: true,
      align: 'right',
      render: (_: number, data: ILmsProfileModel) => String(data.totalCourses),
    },
    {
      title: t(`${translationPrefix}.list.table.headers.users`),
      key: 'total_users',
      sorteable: true,
      align: 'right',
      render: (_: number, data: ILmsProfileModel) => String(data.totalUsers),
    },
    {
      title: t(`${translationPrefix}.list.table.headers.assignmentMode`),
      key: 'profile_type',
      sorteable: true,
      render: (_: number, data: ILmsProfileModel) => (
        <>{String(profileType[data.profileType])}</>
      ),
    },
    {
      width: 116,
      hideable: false,
      align: 'center',
      fixed: 'right',
      render: (data: ILmsProfileModel) => (
        <Options>
          <Link to={createRouteProfileEdit({ id: data.id })}>
            <Button>{t(`${translationPrefix}.list.table.buttons.edit`)}</Button>
          </Link>
          <DeleteProfileButton
            id={data.id}
            count={data.totalUsers}
            hasLearningPath={data.hasLearningPath}
          />
        </Options>
      ),
    },
  ]

  return (
    <>
      <RankmiTableWrapper>
        <RankmiTable
          columnHeaders={columns}
          addButtonIcon={faPlus}
          onAddClick={() => history.push(ProfileRoutes.CREATE)}
          data={paginatedList.slice(
            page * rowsPerPage - rowsPerPage,
            page * rowsPerPage
          )}
          countIcon={faTag}
          search={searchText}
          onSearch={setSearchText}
          total={total}
          locale={locale}
          loading={loading}
          currentPage={page}
          onPaginationChange={setPage}
          initialRowsPerPage={rowsPerPage}
          onRowsPerPageChange={setRowsPerPage}
          onSortChange={onSortChange}
          actions={actions}
          delaySearch
          responsive
        />
      </RankmiTableWrapper>
      <UploadModal show={showUploadModal} setShow={setShowUploadModal} />
    </>
  )
}

export default List
